import { useUser } from '@folklore/auth';
import { useSocket } from '@folklore/socket';
import {
    useQuery,
    keepPreviousData as previousPlaceholder,
    useMutation,
    useInfiniteQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { isAfter } from 'date-fns/isAfter';
import { parseISO } from 'date-fns/parseISO';
import isArray from 'lodash/isArray';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import { useCallback, useMemo, useState } from 'react';

import { useApi } from '../contexts/ApiContext';
import { updateAuthUser } from './useAuth';

export interface CommentsQuery {
    parent?: string | null;
}

export interface CommentsPageResponse {
    data: Comment[];
    total_count?: number | null;
    pagination: {
        page: number;
        last_page: number;
        total: number;
    };
}

function isNewer(first: Comment, second: Comment) {
    return isAfter(
        parseISO(first.updatedAt || first.createdAt),
        parseISO(second.updatedAt || second.createdAt),
    );
}

function sortComment(sort: 'asc' | 'desc' | null = null) {
    return (a: Comment, b: Comment) => {
        const { score: aScore = 0, createdAt: aCreatedAt } = a;
        const { score: bScore = 0, createdAt: bCreatedAt } = b;
        if (aScore !== bScore) {
            return aScore > bScore ? -1 : 1; // Higher score first
        }
        const aDate = parseISO(aCreatedAt);
        const bDate = parseISO(bCreatedAt);
        if (aDate.getTime() === bDate.getTime()) {
            return 0; // Same date, no change
        }
        if (sort === 'desc') {
            return aDate > bDate ? -1 : 1; // Newer date first
        }
        return aDate > bDate ? 1 : -1; // Older date first
    };
}

export function useCommentsList(
    commentableKey: string | null,
    query: CommentsQuery = null,
    opts = null,
) {
    const { enabled = true, sort = null } = opts || {};
    const { items: loadedItems = [], ...queryResult } = useCommentsInfinite(
        commentableKey,
        query,
        opts,
    );
    const user = useUser();
    const { role = 'guest' } = user || {};
    const isAdmin = role === 'admin' || role === 'staff';
    const [updatedComments, setUpdatedComments] = useState<Comment[]>([]);
    const {
        comments: socketComments,
        deletedComments: socketDeletedComments,
        blockedUsers,
    } = useSocketComments(enabled ? commentableKey : null, query);

    const addComment = useCallback(
        (comment: Comment) => {
            setUpdatedComments((currentComments) => [
                ...currentComments.filter(({ id }) => id !== comment.id),
                comment,
            ]);
        },
        [setUpdatedComments],
    );

    const items = useMemo(() => {
        let finalItems = [...loadedItems];
        const newSocketComments = socketComments.reduce((acc, newComment) => {
            const existingIndex = finalItems.findIndex(({ id }) => id === newComment.id);
            if (existingIndex === -1) {
                const updatedIndex = updatedComments.findIndex(({ id }) => id === newComment.id);
                return updatedIndex === -1 || isNewer(newComment, updatedComments[updatedIndex])
                    ? [...acc, newComment]
                    : acc;
            }
            const currentComment = finalItems[existingIndex];
            if (isNewer(newComment, currentComment)) {
                finalItems[existingIndex] = newComment;
            }
            return acc;
        }, []);
        const newUpdatedComments = updatedComments.reduce((acc, newComment) => {
            const existingIndex = finalItems.findIndex(({ id }) => id === newComment.id);
            if (existingIndex === -1) {
                const socketIndex = newSocketComments.findIndex(({ id }) => id === newComment.id);
                return socketIndex === -1 || isNewer(newComment, newSocketComments[socketIndex])
                    ? [...acc, newComment]
                    : acc;
            }
            const currentComment = finalItems[existingIndex];
            if (isNewer(newComment, currentComment)) {
                finalItems[existingIndex] = newComment;
            }
            return acc;
        }, []);
        finalItems = [...finalItems, ...newSocketComments]
            .sort(sortComment(sort))
            .filter(({ id }) => socketDeletedComments.indexOf(id) === -1);
        return [...newUpdatedComments.sort(sortComment(sort)), ...finalItems].filter(
            ({ author: { userId = null } }) =>
                userId === null || blockedUsers.indexOf(userId) === -1 || isAdmin,
        );
    }, [
        loadedItems,
        socketComments,
        updatedComments,
        socketDeletedComments,
        blockedUsers,
        isAdmin,
        sort,
    ]);
    return {
        ...queryResult,
        items,
        addComment,
    };
}

export function useSocketComments(commentableKey: string | null, query: CommentsQuery = null) {
    const { parent = null } = query || {};
    const [comments, setComments] = useState<Comment[]>([]);
    const [deletedComments, setDeletedComments] = useState<Comment[]>([]);
    const [blockedUsers, setBlockedUsers] = useState<string[]>([]);
    const channels = useMemo(
        () => (commentableKey !== null ? [`comments.${commentableKey}`, 'comments.users'] : []),
        [commentableKey],
    );
    const onSocketMessage = useCallback(
        (message) => {
            const { event } = message || {};
            if (event === 'user.blocked') {
                const {
                    data: { id: userId },
                } = message || {};
                setBlockedUsers((currentUsers) => [
                    ...currentUsers.filter((id) => id !== userId),
                    userId,
                ]);
            }

            const { data: socketComment } = message || {};
            const { parentId: socketParentId = null } = socketComment || {};
            if (socketParentId !== parent) {
                return;
            }
            if (event === 'comment.created' || event === 'comment.updated') {
                setComments((currentComments) => [
                    ...currentComments.filter(({ id }) => id !== socketComment.id),
                    socketComment,
                ]);
            } else if (event === 'comment.deleted') {
                setDeletedComments((currentComments) => [
                    ...currentComments.filter((id) => id !== socketComment.id),
                    socketComment.id,
                ]);
            }
        },
        [parent],
    );
    useSocket(channels, {
        onMessage: onSocketMessage,
    });

    return {
        comments,
        deletedComments,
        blockedUsers,
    };
}

export function useCommentsInfinite(
    commentableKey: string | null,
    query: CommentsQuery = null,
    opts = null,
) {
    const api = useApi();
    const { keepPreviousData = true, enabled = true, ...otherOpts } = opts || {};
    const {
        data = null,
        isLoading,
        ...queryResult
    } = useInfiniteQuery<CommentsPageResponse>({
        queryKey: ['comments_infinite', commentableKey, query],
        initialPageParam: 1,
        queryFn: ({ pageParam = 1, queryKey: [, commentableKeyParam, queryParam], signal }) =>
            api.comments.getForCommentable(commentableKeyParam, queryParam, pageParam, null, {
                signal,
            }),
        getNextPageParam: ({ pagination: { page: currentPage = 1, last_page: lastPage = -1 } }) => {
            if (lastPage === currentPage) {
                return undefined;
            }
            return currentPage + 1;
        },
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        enabled: enabled && commentableKey !== null,
        ...otherOpts,
    });

    const { pages = null, pageParams = null } = data || {};

    const items = useMemo(
        () =>
            pages !== null
                ? pages.reduce((allItems, { data: pageItems }) => [...allItems, ...pageItems], [])
                : [],
        [pages],
    );
    const totalForPagination = (pages || []).reduce<number | null>(
        (currentTotal, { pagination: { total: newTotal = 0 } }) =>
            currentTotal === null || newTotal > currentTotal ? newTotal : currentTotal,
        null,
    );
    const total = (pages || []).reduce<number | null>(
        (currentTotal, { total_count: newTotal }) =>
            currentTotal === null || newTotal > currentTotal ? newTotal : currentTotal,
        null,
    );

    return {
        total,
        endIsReached:
            !isLoading &&
            totalForPagination !== null &&
            items !== null &&
            totalForPagination <= items.length,
        items,
        pages,
        pageParams,
        isLoading,
        ...queryResult,
    };
}

export function useComments(commentableKey: string | null, query: CommentsQuery = null, opts = {}) {
    const api = useApi();
    const { keepPreviousData = true } = opts || {};
    const { data = null, ...queryResult } = useQuery<Comment[]>({
        queryKey: ['comments', commentableKey, query],
        queryFn: ({ queryKey: [, commentableKeyParam, queryParam], signal }) =>
            api.comments.getForCommentable(commentableKeyParam, queryParam, null, null, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...opts,
    });

    const { data: items = null, ...metadata } = isArray(data) ? { data } : data || {};

    return data !== null
        ? {
              items,
              ...metadata,
              ...queryResult,
          }
        : queryResult;
}

export interface CreateCommentRequest {
    body: string;
    parent?: string | null;
    email?: string | null;
    phone?: string | null;
}

export interface CreateCommentResponse {
    comment: Comment;
    user: User;
}

export function useCreateComment(commentableKey: string | null) {
    const api = useApi();
    const queryClient = useQueryClient();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<
        CreateCommentResponse,
        Error,
        CreateCommentRequest
    >({
        mutationFn: (data) => api.comments.createForCommentable(commentableKey, data),
        onSuccess: ({ user = null }) => {
            if (user !== null) {
                updateAuthUser(queryClient, user);
            }
        },
    });

    return {
        createComment: mutate,
        createCommentAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useReactComment(commentId: string) {
    const api = useApi();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<Comment, Error, string>({
        mutationFn: (data) => api.comments.react(commentId, data),
    });

    return {
        react: mutate,
        reactAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useDestroyComment(commentId: string) {
    const api = useApi();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<Comment, Error>({
        mutationFn: () => api.comments.destroy(commentId),
    });

    return {
        destroy: mutate,
        destroyAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useRestoreComment(commentId: string) {
    const api = useApi();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<Comment, Error>({
        mutationFn: () => api.comments.restore(commentId),
    });

    return {
        restore: mutate,
        restoreAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useBlockComment(commentId: string) {
    const api = useApi();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<Comment, Error, boolean>({
        mutationFn: (blocked) => api.comments.block(commentId, blocked),
    });

    return {
        block: mutate,
        blockAsync: mutateAsync,
        ...mutationResult,
    };
}

export function usePinComment(commentId: string) {
    const api = useApi();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<Comment, Error, boolean>({
        mutationFn: (pinned) => api.comments.pin(commentId, pinned),
    });

    return {
        pin: mutate,
        pinAsync: mutateAsync,
        ...mutationResult,
    };
}
